import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, X } from "lucide-react";

const FIRST_DELAY_MS = 90 * 1000; // 1.5 min after load
const REPEAT_MS = 8 * 60 * 1000;  // every 8 min
const SNOOZE_KEY = "cookieDonateSnoozeUntil";
const SNOOZE_MS = 24 * 60 * 60 * 1000;

const HIDE_ROUTES = ["/buy-a-cookie", "/cookie-thanks", "/auth", "/onboarding"];

export function DonationPopup() {
  const [open, setOpen] = useState(false);
  const [pathname, setPathname] = useState(() =>
    typeof window !== "undefined" ? window.location.pathname : "/"
  );

  useEffect(() => {
    const update = () => setPathname(window.location.pathname);
    window.addEventListener("popstate", update);
    const id = setInterval(update, 1000); // catch pushState navigations
    return () => {
      window.removeEventListener("popstate", update);
      clearInterval(id);
    };
  }, []);

  const location = { pathname };

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    let interval: ReturnType<typeof setInterval>;

    const isSnoozed = () => {
      const until = Number(localStorage.getItem(SNOOZE_KEY) || 0);
      return Date.now() < until;
    };

    const show = () => {
      if (isSnoozed()) return;
      if (HIDE_ROUTES.some((r) => location.pathname.startsWith(r))) return;
      setOpen(true);
    };

    timer = setTimeout(() => {
      show();
      interval = setInterval(show, REPEAT_MS);
    }, FIRST_DELAY_MS);

    return () => {
      clearTimeout(timer);
      if (interval) clearInterval(interval);
    };
  }, [location.pathname]);

  const dismiss = () => {
    setOpen(false);
    localStorage.setItem(SNOOZE_KEY, String(Date.now() + SNOOZE_MS));
  };

  if (!open) return null;
  if (HIDE_ROUTES.some((r) => location.pathname.startsWith(r))) return null;

  return (
    <div className="fixed bottom-24 left-4 z-50 max-w-xs animate-in slide-in-from-left-4 fade-in duration-300">
      <div className="rounded-2xl border border-amber-300/40 bg-background/95 backdrop-blur-md shadow-2xl p-4 relative">
        <button
          onClick={dismiss}
          className="absolute top-2 right-2 h-6 w-6 rounded-full hover:bg-muted flex items-center justify-center text-muted-foreground"
          aria-label="Dismiss"
        >
          <X className="h-3.5 w-3.5" />
        </button>
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
            <Cookie className="h-5 w-5 text-amber-700" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-sm">Enjoying SaveySpend?</div>
            <p className="text-xs text-muted-foreground mt-0.5 mb-2">
              Buy us a cookie 🍪 and keep SaveySpend free!
            </p>
            <Link
              to="/buy-a-cookie"
              onClick={() => setOpen(false)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-500 hover:bg-amber-600 text-white text-xs font-semibold transition-colors"
            >
              Donate
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
